import React, { useCallback, useMemo } from 'react';
import {
  Archive,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Mail,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { BriefingItem } from '@/api/briefing';
import type { Email } from '@/types/email';
import { SwipeableRow, type SwipeActionConfig } from './SwipeableRow';
import {
  BriefingChips,
  BriefingDetails,
  BriefingEntries,
  BriefingLegacyBody,
  BriefingMarkdown,
  BriefingNextStep,
  BriefingQuote,
  BriefingRelated,
  BriefingStructuredChips,
  KIND_CONFIG,
  parseBriefingConventions,
  titleizeKind,
  type BriefingRelatedRef,
} from './briefingShared';

export interface BriefingRowRef {
  email: Email;
  /** Message currently has an inbox membership. */
  inInbox: boolean;
  /** The email object came from the live list projection (actions resolve). */
  isLive: boolean;
}

const formatRefDate = (value: Email['date']): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const refSenderLabel = (email: Email): string =>
  email.from?.name?.trim() || email.from?.email || 'Unknown sender';

// ---------------------------------------------------------------------------
// BriefingItemRow — one briefing item as a first-class inbox row. Item-first
// hierarchy (AI title, chips, markdown note); the referenced email's identity
// is the small footer line. Actions: click opens the email (1 ref) / drills
// down (multi-ref) / expands (0 refs); Done dismisses the item only.
// ---------------------------------------------------------------------------

export interface BriefingItemRowProps {
  item: BriefingItem;
  refs: BriefingRowRef[];
  isPending: boolean;
  onMarkDone: (item: BriefingItem) => void;
  /** Compound action: archive in-inbox refs + resolve the item as actioned. */
  onArchiveDone?: (item: BriefingItem, refs: BriefingRowRef[]) => void;
  onOpenEmail: (
    emailId: string,
    folderId?: string | null,
    accountId?: string | null,
    threadId?: string | null
  ) => void;
  /** Receives the row's RESOLVED refs so the drill-down matches the card. */
  onEnterItemScope: (item: BriefingItem, refs: BriefingRowRef[]) => void;
  swipeDisabled?: boolean;
  /** Scope-header variant: full body, no row navigation, no swipe. */
  fullDetail?: boolean;
  /** data.related_keys resolved to live items (key + title), for "see also" links. */
  related?: BriefingRelatedRef[];
  onOpenRelated?: (key: string) => void;
}

function BriefingItemRowInner({
  item,
  refs,
  isPending,
  onMarkDone,
  onArchiveDone,
  onOpenEmail,
  onEnterItemScope,
  swipeDisabled = false,
  fullDetail = false,
  related,
  onOpenRelated,
}: BriefingItemRowProps) {
  const config = KIND_CONFIG[item.kind];
  const Icon = config?.icon ?? Sparkles;
  const colorClass = config?.colorClass ?? 'text-primary';
  const conventions = useMemo(() => parseBriefingConventions(item), [item]);

  const primaryRef = refs.length > 0 ? refs[0] : null;
  const archivedCount = refs.filter((ref) => !ref.inInbox).length;

  // Refs in the same conversation collapse to ONE row in the drill-down (the
  // list projection threads by account:folder:thread), so count conversations
  // too: the footer explains the difference, and an all-one-conversation item
  // opens the conversation reader directly — a one-row drill-down is noise.
  const conversationCount = useMemo(() => {
    const keys = new Set<string>();
    refs.forEach((ref) => {
      keys.add(`${ref.email.accountId}:${ref.email.threadId ?? `msg-${ref.email.id}`}`);
    });
    return keys.size;
  }, [refs]);
  const opensSingleConversation = refs.length > 1 && conversationCount === 1;

  const hasRowAction = !fullDetail && !isPending && refs.length > 0;

  const handleClick = useCallback(() => {
    if (fullDetail || isPending) {
      return;
    }
    if (refs.length === 1 || opensSingleConversation) {
      if (!primaryRef) {
        return;
      }
      // For a single conversation, open on the newest referenced message.
      const target = refs.reduce((best, ref) => {
        const bestTime = new Date(best.email.date as unknown as string).getTime() || 0;
        const refTime = new Date(ref.email.date as unknown as string).getTime() || 0;
        return refTime > bestTime ? ref : best;
      }, primaryRef);
      onOpenEmail(
        target.email.id,
        target.email.folderId ?? null,
        target.email.accountId ?? item.accountId,
        target.email.threadId ?? null
      );
      return;
    }
    if (refs.length > 1) {
      onEnterItemScope(item, refs);
    }
  }, [fullDetail, isPending, refs, primaryRef, opensSingleConversation, onOpenEmail, onEnterItemScope, item]);

  const canArchiveDone = Boolean(onArchiveDone) && refs.some((ref) => ref.inInbox);

  const swipeActions = useMemo<SwipeActionConfig[]>(
    () => [
      {
        key: 'done',
        label: 'Done',
        icon: <Check className="h-4 w-4" />,
        tone: 'success',
        commitStyle: 'fly',
        onAction: () => onMarkDone(item),
      },
    ],
    [item, onMarkDone]
  );

  // Left rail mirrors the email rows' archive swipe, but as the compound
  // "archive + resolve" — only offered while a referenced email is in the inbox.
  const leftSwipeActions = useMemo<SwipeActionConfig[]>(
    () =>
      canArchiveDone
        ? [
            {
              key: 'archive-done',
              label: 'Archive',
              icon: <Archive className="h-4 w-4" />,
              tone: 'primary',
              commitStyle: 'fly',
              onAction: () => onArchiveDone!(item, refs),
            },
          ]
        : [],
    [canArchiveDone, onArchiveDone, item, refs]
  );

  // Single live ref: expose the referenced email to the keyboard registry via
  // the standard row attribute, so quick actions (archive/read/…) act on the
  // underlying email exactly like an email row. Multi/zero-ref and non-live
  // refs expose nothing — Done stays click/swipe only.
  const keyboardEmailId = refs.length === 1 && primaryRef?.isLive ? primaryRef.email.id : undefined;

  const card = (
    <div
      data-briefing-item-key={`${item.accountId}:${item.key}`}
      data-email-row-id={keyboardEmailId}
      className={cn(
        'group relative flex w-full overflow-hidden rounded-2xl border border-border/60 bg-card shadow-xs transition-colors',
        hasRowAction && 'cursor-pointer hover:bg-accent/40',
        isPending && 'pointer-events-none opacity-50'
      )}
      onClick={hasRowAction ? handleClick : undefined}
    >
      <div className="min-w-0 flex-1 p-3">
        <div className="flex items-start gap-2">
          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', colorClass)} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-semibold leading-snug">{item.title}</span>
              <BriefingChips c={conventions} />
            </div>

            <BriefingStructuredChips c={conventions} />

            {conventions.summaryMd ? (
              <div className="mt-1">
                <BriefingMarkdown content={conventions.summaryMd} />
              </div>
            ) : null}

            {conventions.quote ? <BriefingQuote quote={conventions.quote} /> : null}

            {conventions.entries.length > 0 ? <BriefingEntries entries={conventions.entries} /> : null}

            {conventions.nextStep ? <BriefingNextStep nextStep={conventions.nextStep} /> : null}

            {item.bodyMd ? (
              conventions.summaryMd || conventions.entries.length > 0 ? (
                // New-format item: the body is the evidence/details section,
                // collapsed behind an expander (open in the drill-down header).
                <BriefingDetails bodyMd={item.bodyMd} defaultOpen={fullDetail} />
              ) : (
                <BriefingLegacyBody bodyMd={item.bodyMd} fullDetail={fullDetail} />
              )
            ) : null}

            {related && related.length > 0 ? (
              <BriefingRelated related={related} onOpenRelated={onOpenRelated} />
            ) : null}

            {/* Bottom line: email identity on the left, actions on the right —
                the actions share this line instead of occupying a full-height
                right column, so the title/note get the row's full width. */}
            <div className="mt-1.5 flex items-end justify-between gap-2">
              <div className="min-w-0 flex-1">
                {refs.length === 1 && primaryRef ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="shrink-0 font-medium text-foreground/80">
                      {refSenderLabel(primaryRef.email)}
                    </span>
                    <span className="min-w-0 truncate">— {primaryRef.email.subject}</span>
                    <span className="shrink-0">{formatRefDate(primaryRef.email.date)}</span>
                    {!primaryRef.inInbox ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                        <Archive className="h-2.5 w-2.5" />
                        Archived
                      </span>
                    ) : null}
                    {!primaryRef.email.read ? (
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-label="Unread" />
                    ) : null}
                  </div>
                ) : refs.length > 1 ? (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span>
                      {refs.length} messages
                      {conversationCount < refs.length
                        ? ` · ${conversationCount === 1 ? '1 conversation' : `${conversationCount} conversations`}`
                        : ''}
                    </span>
                    {archivedCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                        <Archive className="h-2.5 w-2.5" />
                        {archivedCount === refs.length ? 'All archived' : `${archivedCount} archived`}
                      </span>
                    ) : null}
                    {!fullDetail && !opensSingleConversation ? <ChevronRight className="h-3 w-3" /> : null}
                  </div>
                ) : null}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {canArchiveDone ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          onArchiveDone!(item, refs);
                        }}
                      >
                        <Archive className="h-3 w-3" />
                        Archive
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Archive the email and mark this item done</TooltipContent>
                  </Tooltip>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkDone(item);
                  }}
                >
                  <Check className="h-3 w-3" />
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (fullDetail) {
    return card;
  }

  return (
    <SwipeableRow leftActions={leftSwipeActions} rightActions={swipeActions} disabled={swipeDisabled}>
      {card}
    </SwipeableRow>
  );
}

const areBriefingItemRowPropsEqual = (
  prev: Readonly<BriefingItemRowProps>,
  next: Readonly<BriefingItemRowProps>
): boolean => {
  if (
    prev.item !== next.item ||
    prev.isPending !== next.isPending ||
    prev.swipeDisabled !== next.swipeDisabled ||
    prev.fullDetail !== next.fullDetail ||
    prev.onMarkDone !== next.onMarkDone ||
    prev.onArchiveDone !== next.onArchiveDone ||
    prev.onOpenEmail !== next.onOpenEmail ||
    prev.onEnterItemScope !== next.onEnterItemScope ||
    prev.onOpenRelated !== next.onOpenRelated
  ) {
    return false;
  }
  const prevRelated = prev.related ?? [];
  const nextRelated = next.related ?? [];
  if (prevRelated.length !== nextRelated.length) {
    return false;
  }
  for (let i = 0; i < prevRelated.length; i++) {
    if (prevRelated[i].key !== nextRelated[i].key || prevRelated[i].title !== nextRelated[i].title) {
      return false;
    }
  }
  if (prev.refs.length !== next.refs.length) {
    return false;
  }
  for (let i = 0; i < prev.refs.length; i++) {
    if (
      prev.refs[i].email !== next.refs[i].email ||
      prev.refs[i].inInbox !== next.refs[i].inInbox ||
      prev.refs[i].isLive !== next.refs[i].isLive
    ) {
      return false;
    }
  }
  return true;
};

export const BriefingItemRow = React.memo(BriefingItemRowInner, areBriefingItemRowPropsEqual);

// ---------------------------------------------------------------------------
// BriefingSectionRow — collapsible kind section header on the briefing page:
// chevron + icon + label + count. Clicking toggles the section.
// ---------------------------------------------------------------------------

export interface BriefingSectionRowProps {
  kind: string;
  collapsed: boolean;
  onToggle: (kind: string) => void;
}

function BriefingSectionRowInner({ kind, collapsed, onToggle }: BriefingSectionRowProps) {
  const config = KIND_CONFIG[kind];
  const label = config?.label ?? titleizeKind(kind);
  const Chevron = collapsed ? ChevronRight : ChevronDown;

  return (
    <button
      type="button"
      data-briefing-section={kind}
      className="flex w-full items-center gap-2 rounded-full px-2 py-1.5 mt-2 text-left transition-colors hover:bg-accent/60"
      onClick={() => onToggle(kind)}
      aria-expanded={!collapsed}
    >
      <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="text-xs font-semibold uppercase tracking-wide text-foreground/80">{label}</span>
    </button>
  );
}

export const BriefingSectionRow = React.memo(BriefingSectionRowInner);

// ---------------------------------------------------------------------------
// BriefingScopeHeader — header for the item drill-down: back button + the
// full item card (title, chips, complete note, Done) above its referenced
// emails.
// ---------------------------------------------------------------------------

export interface BriefingScopeHeaderProps {
  item?: BriefingItem | null;
  refs?: BriefingRowRef[];
  isPending?: boolean;
  onExit: () => void;
  onMarkDone: (item: BriefingItem) => void;
  onArchiveDone?: (item: BriefingItem, refs: BriefingRowRef[]) => void;
  related?: BriefingRelatedRef[];
  onOpenRelated?: (key: string) => void;
}

export function BriefingScopeHeader({
  item,
  refs,
  isPending = false,
  onExit,
  onMarkDone,
  onArchiveDone,
  related,
  onOpenRelated,
}: BriefingScopeHeaderProps) {
  return (
    <div className="border-b px-3 py-2">
      <div className="mb-2 flex items-center gap-2">
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={onExit}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <span className="text-xs text-muted-foreground">Briefing item</span>
      </div>
      {item ? (
        <BriefingItemRow
          item={item}
          refs={refs ?? []}
          isPending={isPending}
          fullDetail
          onMarkDone={onMarkDone}
          onArchiveDone={onArchiveDone}
          onOpenEmail={() => undefined}
          onEnterItemScope={() => undefined}
          related={related}
          onOpenRelated={onOpenRelated}
        />
      ) : null}
    </div>
  );
}
